import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Music, Shuffle, TrendingUp, TrendingDown, Disc, ArrowRight, Plus, ExternalLink, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { runAiJob } from '../utils/aiJobs';

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = API_URL;

// Tipos de mezcla disponibles
const MIX_TYPES = [
  {
    id: 'harmonic',
    label: 'Mezcla Armónica',
    description: 'Tonalidades compatibles (Camelot wheel)',
    icon: Music,
    color: 'bg-purple-600/30 text-purple-300 border-purple-500/50'
  },
  {
    id: 'ascending',
    label: 'Subir Energía',
    description: 'Canciones con más energía',
    icon: TrendingUp,
    color: 'bg-orange-600/30 text-orange-300 border-orange-500/50'
  },
  {
    id: 'descending',
    label: 'Bajar Energía',
    description: 'Canciones más suaves',
    icon: TrendingDown,
    color: 'bg-blue-600/30 text-blue-300 border-blue-500/50'
  },
  {
    id: 'same_style',
    label: 'Mismo Estilo',
    description: 'Solo canciones del mismo género',
    icon: Disc,
    color: 'bg-cyan-600/30 text-cyan-300 border-cyan-500/50'
  },
  {
    id: 'transition',
    label: 'Cambiar Género',
    description: 'Pasar a otro estilo musical',
    icon: ArrowRight,
    color: 'bg-pink-600/30 text-pink-300 border-pink-500/50'
  },
  {
    id: 'same_bpm',
    label: 'Mismo BPM',
    description: 'Tempo similar (±5 BPM)',
    icon: Shuffle,
    color: 'bg-green-600/30 text-green-300 border-green-500/50'
  }
];

const MixSuggestionsModal = ({ 
  isOpen, 
  onClose, 
  song, 
  token, 
  onInsertAfter,
  onAddToLibrary 
}) => {
  const [selectedMixTypes, setSelectedMixTypes] = useState(['harmonic']);
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [preferLibrary, setPreferLibrary] = useState(true);

  const toggleMixType = (typeId) => {
    setSelectedMixTypes(prev => 
      prev.includes(typeId) 
        ? prev.filter(t => t !== typeId)
        : [...prev, typeId]
    );
  };

  const handleSearch = async () => {
    if (selectedMixTypes.length === 0) {
      toast.error('Selecciona al menos un tipo de mezcla');
      return;
    }

    setIsLoading(true);
    setHasSearched(true);
    
    try {
      const response = await runAiJob({
        startUrl: `${API}/api/dj-assistant/mix-suggestions/start`,
        statusBase: `${API}/api/dj-assistant/jobs`,
        body: {
          song_id: song.id,
          song_title: song.title || song.titulo,
          song_artist: song.artist || song.artista,
          song_genre: song.genres?.[0] || song.genero || song.estilo,
          song_bpm: song.bpm,
          song_key: song.key || song.tonalidad,
          mix_types: selectedMixTypes,
          num_suggestions: 8,
          prefer_library: preferLibrary
        },
        config: {
          headers: { Authorization: `Bearer ${token}` }
        },
      });

      if (response.data.success) {
        setSuggestions(response.data.suggestions || []);
        toast.success(`${response.data.suggestions?.length || 0} sugerencias encontradas`);
      } else {
        toast.error('Error al obtener sugerencias');
      }
    } catch (error) {
      console.error('Error fetching mix suggestions:', error);
      toast.error(error.response?.data?.detail || 'Error al obtener sugerencias');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddSuggestionToPlaylist = (suggestion) => {
    if (onInsertAfter && suggestion.library_song_id) {
      // Insertar justo después de la canción base
      onInsertAfter(suggestion.library_song_id, suggestion.library_song_id);
    } else if (onAddToLibrary) {
      // Si no está en biblioteca, añadirla primero
      onAddToLibrary(suggestion);
    }
  };

  const openSpotifyLink = (url, e) => {
    e.stopPropagation();
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    if (isMobile) {
      window.location.href = url;
    } else {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  // Reset state when modal opens with new song
  React.useEffect(() => {
    if (isOpen) {
      setSuggestions([]);
      setHasSearched(false);
    }
  }, [isOpen, song?.id]);

  if (!song) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl w-[95vw] max-h-[90vh] overflow-y-auto bg-slate-900 border-slate-700">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2 text-base">
            <Shuffle className="w-5 h-5 text-purple-400" />
            Sugerencias de Mezcla
          </DialogTitle>
          <DialogDescription className="text-slate-400 text-sm">
            ¿Qué mezcla bien con "{song.title || song.titulo}"?
          </DialogDescription>
        </DialogHeader>

        {/* Canción base */}
        <div className="bg-slate-800/50 p-2 sm:p-3 rounded-lg border border-slate-700 mb-4">
          <p className="text-xs text-slate-400">Seleccionada:</p>
          <p className="text-white font-semibold text-sm truncate">{song.title || song.titulo} - {song.artist || song.artista}</p>
          <div className="flex flex-wrap gap-1 mt-2 text-xs">
            {song.genres?.[0] && (
              <span className="px-2 py-1 bg-cyan-600/30 text-cyan-300 rounded">{song.genres[0]}</span>
            )}
            {song.bpm && (
              <span className="px-2 py-1 bg-slate-700 text-slate-300 rounded">{song.bpm} BPM</span>
            )}
            {(song.key || song.tonalidad) && (
              <span className="px-2 py-1 bg-purple-600/30 text-purple-300 rounded">Key: {song.key || song.tonalidad}</span>
            )}
            {song.energia && (
              <span className={`px-2 py-1 rounded ${
                song.energia === 'Muy Alta' || song.energia === 'Alta' ? 'bg-orange-600/30 text-orange-300' :
                song.energia === 'Media' ? 'bg-yellow-600/30 text-yellow-300' :
                'bg-blue-600/30 text-blue-300'
              }`}>
                {song.energia}
              </span>
            )}
          </div>
        </div>

        {/* Tipos de mezcla */}
        <div className="space-y-2 mb-4">
          <p className="text-white font-semibold text-sm">Tipo de mezcla:</p>
          <div className="grid grid-cols-2 gap-1.5 sm:gap-2">
            {MIX_TYPES.map((type) => {
              const Icon = type.icon;
              const isSelected = selectedMixTypes.includes(type.id);
              return (
                <button
                  key={type.id}
                  onClick={() => toggleMixType(type.id)}
                  data-testid={`mix-type-${type.id}`}
                  className={`p-2 sm:p-3 rounded-lg border transition-all text-left ${
                    isSelected 
                      ? `${type.color} border-2` 
                      : 'bg-slate-800/50 border-slate-700 text-slate-400 hover:bg-slate-800'
                  }`}
                >
                  <div className="flex items-center gap-1.5">
                    <Icon className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                    <span className="font-medium text-xs sm:text-sm truncate">{type.label}</span>
                  </div>
                  <p className="text-[10px] sm:text-xs opacity-70 mt-0.5 line-clamp-1">{type.description}</p>
                </button>
              );
            })}
          </div>
        </div>

        {/* Opciones */}
        <div className="flex items-center gap-2 mb-3 p-2 bg-slate-800/30 rounded-lg">
          <Checkbox 
            id="prefer-library"
            checked={preferLibrary}
            onCheckedChange={setPreferLibrary}
          />
          <label htmlFor="prefer-library" className="text-xs sm:text-sm text-slate-300 cursor-pointer">
            Priorizar mi biblioteca
          </label>
        </div>

        {/* Botón buscar */}
        <Button
          onClick={handleSearch}
          disabled={isLoading || selectedMixTypes.length === 0}
          className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white mb-3 text-sm"
          data-testid="search-mix-suggestions-btn"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Buscando...
            </>
          ) : (
            <>
              <Shuffle className="w-4 h-4 mr-2" />
              Buscar
            </>
          )}
        </Button>

        {/* Resultados */}
        {hasSearched && (
          <div className="space-y-2">
            <p className="text-white font-semibold text-xs sm:text-sm">
              {suggestions.length > 0 
                ? `${suggestions.length} sugerencias:` 
                : 'Sin resultados'}
            </p>
            
            {suggestions.map((suggestion, index) => (
              <div 
                key={`${suggestion.title}-${index}`}
                className="bg-slate-800/50 p-2 sm:p-3 rounded-lg border border-slate-700"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0 overflow-hidden">
                    <p className="text-white font-medium text-xs sm:text-sm truncate">
                      {suggestion.title}
                    </p>
                    <p className="text-slate-400 text-xs truncate">
                      {suggestion.artist}
                    </p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {suggestion.source === 'library' && (
                        <span className="px-1.5 py-0.5 bg-green-600/30 text-green-300 rounded text-[10px]">
                          Biblioteca
                        </span>
                      )}
                      {suggestion.source === 'master_dj' && (
                        <span className="px-1.5 py-0.5 bg-purple-600/30 text-purple-300 rounded text-[10px]">
                          IA
                        </span>
                      )}
                      {suggestion.bpm && (
                        <span className="px-1.5 py-0.5 bg-slate-700 text-slate-300 rounded text-[10px]">
                          {suggestion.bpm}
                        </span>
                      )}
                    </div>
                    {suggestion.mix_reason && (
                      <p className="text-[10px] sm:text-xs text-slate-400 mt-1 line-clamp-2">
                        {suggestion.mix_reason}
                      </p>
                    )}
                  </div>
                  
                  <div className="flex flex-col gap-1 flex-shrink-0">
                    {suggestion.in_library || suggestion.source === 'library' ? (
                      <Button
                        onClick={() => handleAddSuggestionToPlaylist(suggestion)}
                        size="sm"
                        className="bg-cyan-600 hover:bg-cyan-700 text-white text-[10px] sm:text-xs px-2 h-7"
                        data-testid={`add-suggestion-${index}`}
                      >
                        <Plus className="w-3 h-3 sm:mr-1" />
                        <span className="hidden sm:inline">Añadir</span>
                      </Button>
                    ) : (
                      <Button
                        onClick={() => onAddToLibrary && onAddToLibrary(suggestion)}
                        size="sm"
                        variant="outline"
                        className="border-purple-500 text-purple-300 hover:bg-purple-600/20 text-[10px] sm:text-xs px-2 h-7"
                        data-testid={`add-to-library-${index}`}
                      >
                        <Plus className="w-3 h-3 sm:mr-1" />
                        <span className="hidden sm:inline">Biblio</span>
                      </Button>
                    )}
                    {suggestion.spotify_url && (
                      <Button
                        onClick={(e) => openSpotifyLink(suggestion.spotify_url, e)}
                        size="sm"
                        variant="ghost"
                        className="text-green-400 hover:bg-green-600/20 text-[10px] px-2 h-6"
                      >
                        <ExternalLink className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MixSuggestionsModal;
